/**
 * The tags of a business (docs/08-entscheidungen.md, S17): a list in a fixed order, kept like the
 * categories (S12). A dish has some of them; the server turns away any other. The name is the key
 * (without regard to case): renaming a tag renames it at every dish and on every menu that shows it;
 * removing it takes it off the dishes. On the card a tag is written as in the list.
 */
import { LIMITS } from "./dishes.ts";
import { tagIcon } from "./tag-icons.ts";

export const MAX_TAGS = 100;

const oneLine = (s: unknown) => (typeof s === "string" ? s.normalize("NFC").replace(/\s+/g, " ").trim() : "");
export const cleanTag = (raw: unknown) => oneLine(raw).slice(0, LIMITS.tag);
export const tagKey = (name: string) => name.toLocaleLowerCase("de-DE");

/** A tag as entered in the list; `from`: the name it had, when it was renamed; `icon`: its symbol (S21). */
export interface TagEntry {
  name: string;
  from?: string;
  icon?: string;
}

/** The list as entered: names cleaned, each once (without regard to case), none empty. */
export function cleanTagList(input: unknown): { value: TagEntry[] } | { error: string } {
  if (!Array.isArray(input)) return { error: "Ungültige Liste" };
  if (input.length > MAX_TAGS) return { error: `Höchstens ${MAX_TAGS} Tags` };
  const out: TagEntry[] = [];
  for (const raw of input) {
    const o = (raw ?? {}) as Record<string, unknown>;
    const name = cleanTag(o.name);
    if (name === "") return { error: "Bitte jedem Tag einen Namen geben." };
    if (out.some((t) => tagKey(t.name) === tagKey(name))) return { error: `Der Tag „${name}“ steht zweimal in der Liste.` };
    const from = typeof o.from === "string" && o.from.trim() !== "" ? cleanTag(o.from) : undefined;
    const icon = typeof o.icon === "string" && o.icon !== "" ? o.icon : undefined;
    if (icon && !tagIcon(icon)) return { error: `Unbekanntes Symbol bei „${name}“` };
    out.push({ name, ...(from && from !== name ? { from } : {}), ...(icon ? { icon } : {}) });
  }
  return { value: out };
}

/** The tags of the list a dish means, written as in the list; an unknown one is an error. */
export function resolveTags(raw: readonly string[], list: readonly string[]): { value: string[] } | { error: string } {
  const out: string[] = [];
  for (const t of raw) {
    const found = list.find((x) => tagKey(x) === tagKey(t));
    if (!found) return { error: `Unbekannter Tag „${t}“` };
    if (!out.includes(found)) out.push(found);
  }
  return { value: out };
}

/** The tags the dishes have, in the order of their numbers (for a business before S17). */
export function tagsOfDishes(dishes: Iterable<{ tags: readonly string[]; number: number }>): string[] {
  const out: string[] = [];
  for (const d of [...dishes].sort((a, b) => a.number - b.number)) for (const t of d.tags) if (!out.some((x) => tagKey(x) === tagKey(t))) out.push(t);
  return out;
}
